import { useNavigate } from "react-router-dom";
import { axiosProtected } from "../../api/axios";
import { useAuthContext } from "../../_helper/Auth";
import { useRefreshToken } from "./useRefreshToken";
import { useCallback, useEffect, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { saveRedirectPath } from "../redirectStore";

import { useTranslation } from "react-i18next";
import {
  readPasswordChangeRequiredFromPayload,
  readEmailVerificationFromPayload,
} from "../authPayloadGates";
import { MANDATORY_CHANGE_PASSWORD_PATH } from "../../Routes/PrivateRoute";

// Shared across every useAxios instance — prevents multiple concurrent refresh
// calls when many components are mounted and a 401 fires simultaneously.
let _sharedRefreshPromise = null;

export function useAxios() {
  const {
    token,
    addToken,
    removeToken,
    removeUser,
    setEmailVerificationPending,
    setPasswordChangeRequired,
    setIsInternelServerError,
    setIsForbidden,
  } = useAuthContext();
  const refreshToken = useRefreshToken();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  /** Keep latest URL for interceptor — avoids stale closure vs router updates. */
  const locationRef = useRef({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  useLayoutEffect(() => {
    locationRef.current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
  });

  const getSafeStoredToken = useCallback(() => {
    const accessToken =
      token?.access_token || localStorage.getItem("access_token");
    const refreshToken =
      token?.refresh_token || localStorage.getItem("refresh_token");

    return {
      access_token:
        accessToken && accessToken !== "null" && accessToken !== "undefined"
          ? accessToken
          : null,
      refresh_token:
        refreshToken && refreshToken !== "null" && refreshToken !== "undefined"
          ? refreshToken
          : null,
    };
  }, [token]);

  useEffect(() => {
    const reqInterceptor = axiosProtected.interceptors.request.use(
      (config) => {
        const safeTokens = getSafeStoredToken();
        config.headers = config.headers || {};

        // Avoid leaking an invalid Authorization header.
        if (
          config.headers.Authorization === "Bearer null" ||
          config.headers.Authorization === "Bearer undefined"
        ) {
          delete config.headers.Authorization;
        }

        if (!config.headers.Authorization && safeTokens.access_token) {
          config.headers.Authorization = `Bearer ${safeTokens.access_token}`;
        }

        if (!config.headers.Accept && config.responseType !== "blob") {
          config.headers.Accept = "application/json";
        }

        config.headers["Accept-Language"] =
          (i18n.language === "ae" ? "ar" : "en") || "ar";

        return config;
      },
      (err) => Promise.reject(err)
    );

    const resInterceptor = axiosProtected.interceptors.response.use(
      (res) => res,
      async (err) => {
        const originalReq = err?.config;

        if (err?.response?.status === 500) {
          setIsInternelServerError(true);
          return Promise.reject(err);
        }

        if (err?.response?.status === 403) {
          setIsForbidden(true);
          return Promise.reject(err);
        }

        if (
          err.code === "ECONNABORTED" ||
          err.message === "timeout of 30000ms exceeded"
        ) {
          console.error("Request timeout:", err.config?.url);
          return Promise.reject(
            new Error(
              "Request timeout. Please check your connection and try again."
            )
          );
        }

        if (err.message === "Network Error" || !err.response) {
          console.error("Network error:", err);
          return Promise.reject(
            new Error("Network error. Please check your connection.")
          );
        }

        if (
          err?.response?.status === 401 &&
          !originalReq._retry &&
          !originalReq.url.includes("refresh")
        ) {
          originalReq._retry = true;

          if (!_sharedRefreshPromise) {
            const safeTokens = getSafeStoredToken();
            _sharedRefreshPromise = refreshToken(safeTokens)
              .then((newTokens) => {
                // useRefreshToken already guarantees newTokens.access_token,
                // but double-check so we never set Authorization to "Bearer undefined".
                if (!newTokens?.access_token) {
                  const e = new Error("Missing access token from refresh response");
                  e.refreshResponse = newTokens;
                  throw e;
                }

                addToken({
                  access_token: newTokens.access_token,
                  // keep the existing refresh_token if server doesn't issue a new one
                  refresh_token: newTokens.refresh_token ?? safeTokens.refresh_token,
                  expires_in: newTokens.expires_in,
                });

                const emailPending = readEmailVerificationFromPayload(newTokens);
                if (emailPending !== undefined) {
                  setEmailVerificationPending(emailPending);
                }

                const pwdRequired =
                  readPasswordChangeRequiredFromPayload(newTokens);
                if (pwdRequired !== undefined) {
                  setPasswordChangeRequired(pwdRequired);
                }

                const loc = locationRef.current;
                if (pwdRequired === true) {
                  if (loc.pathname !== MANDATORY_CHANGE_PASSWORD_PATH) {
                    navigate(MANDATORY_CHANGE_PASSWORD_PATH, { replace: true });
                  }
                } else if (
                  emailPending === true &&
                  loc.pathname !== "/verify-email"
                ) {
                  navigate("/verify-email", { replace: true });
                }

                return newTokens;
              })
              .catch((refreshErr) => {
                const status = refreshErr?.response?.status;
                // Only end the session when the refresh endpoint rejects credentials.
                // Network errors, timeouts, and 5xx must not clear the session (matches
                // useProactiveAccessTokenRefresh).
                const isAuthFailure = status === 401 || status === 403;
                if (isAuthFailure) {
                  console.warn(
                    `[auth] refresh rejected by server (status ${status}) → logging out.`
                  );
                  const loc = locationRef.current;
                  saveRedirectPath(loc.pathname, loc.search, loc.hash);
                  removeToken();
                  removeUser();
                  navigate("/login", {
                    replace: true,
                    state: {
                      from: {
                        pathname: loc.pathname,
                        search: loc.search,
                        hash: loc.hash,
                      },
                    },
                  });
                } else {
                  console.warn(
                    "[auth] refresh failed transiently — keeping session:",
                    refreshErr?.message ?? status ?? refreshErr
                  );
                }
                throw refreshErr;
              })
              .finally(() => {
                _sharedRefreshPromise = null;
              });
          }

          // Await the refresh — if it fails (auth error), the catch above already
          // navigated to /login and re-threw, so we just propagate.
          let newTokens;
          try {
            newTokens = await _sharedRefreshPromise;
          } catch (refreshErr) {
            return Promise.reject(refreshErr);
          }

          // Refresh succeeded — retry the original request.
          // A failure here is NOT an auth problem; reject without logging out.
          originalReq.headers = originalReq.headers || {};
          originalReq.headers.Authorization = `Bearer ${newTokens.access_token}`;
          return axiosProtected(originalReq);
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axiosProtected.interceptors.request.eject(reqInterceptor);
      axiosProtected.interceptors.response.eject(resInterceptor);
    };
  }, [
    token,
    addToken,
    removeToken,
    removeUser,
    setEmailVerificationPending,
    setPasswordChangeRequired,
    navigate,
    refreshToken,
    i18n.language,
    setIsForbidden,
    setIsInternelServerError,
    getSafeStoredToken,
  ]);

  return { axiosProtected };
}
